import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import skuUtils from 'utils/Sku';
import { Text, Image, Link } from 'components/ui';
import InfoButton from 'components/InfoButton/InfoButton';
import anaConsts from 'analytics/constants';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import anaUtils from 'analytics/utils';
import klarnaUtils from 'utils/Klarna';
import orderUtils from 'utils/Order';
import helpersUtils from 'utils/Helpers';
import localeUtils from 'utils/LanguageLocale';
import userUtils from 'utils/User';
import productPageSOTBindings from 'analytics/bindingMethods/pages/productPage/productPageSOTBindings';
import Actions from 'actions/Actions';
import { PAYMENT_METHODS } from 'constants/PaymentMethods';
import skuHelpers from 'utils/skuHelpers';
import { colors, radii, space } from 'style/config';
import KlarnaEN from 'components/Klarna/locales/Klarna_en_US';
import KlarnaFR from 'components/Klarna/locales/Klarna_fr_CA';
import { UserInfoReady } from 'constants/events';

const { KLARNA, AFTERPAY, PAYPAL } = PAYMENT_METHODS;
const getText = localeUtils.getLocalisedDataFromResource(KlarnaEN, KlarnaFR);
const { getProp, deferTaskExecution } = helpersUtils;

// eslint-disable-next-line complexity
const KlarnaMarketing = ({
    sku,
    analyticsContext,
    isAfterpayEnabled,
    isKlarnaEnabled,
    isPayPalPayLaterEligibleEnabled,
    showPDPTopOfPageRedesign,
    summaryDropdownDisplay,
    summaryDoubleDisplay,
    showHighlightBnplOnPdp,
    isSmallView
}) => {
    const dispatch = useDispatch();
    const [showCreditCardOffer, setShowCreditCardOffer] = useState(false);
    const [discountValue, setDiscountValue] = useState(undefined);
    const [isUserInfoReady, setIsUserInfoReady] = useState(false);
    const user = useSelector(state => state.user, shallowEqual);

    useEffect(() => {
        skuUtils.getDiscountValue(sku).then(setDiscountValue);
        Sephora.Util.onLastLoadEvent(window, [UserInfoReady], () => setIsUserInfoReady(true));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isUserInfoReady) {
            return;
        }

        const userAudience = (isSmallView && userUtils.isRouge()) || (!isSmallView && userUtils.isUserAtleastRecognized());
        setShowCreditCardOffer(localeUtils.isUS() && userAudience && !userUtils.isSephoraCreditCardHolder());
    }, [user, isSmallView, isUserInfoReady]);

    const triggerAsyncPageLoadEvent = () => {
        const contextEvent = analyticsContext ? anaUtils.getLastAsyncPageLoadData({ pageType: analyticsContext }) : {};
        const pageType = anaConsts.PAGE_TYPES.PRODUCT;
        const pageDetail = showCreditCardOffer
            ? anaConsts.PAGE_DETAIL.SEPHORA_CC_INFO
            : klarnaUtils.getPageDetail(isAfterpayEnabled, isKlarnaEnabled);

        pdpAnalytics.trackPageLoad({
            pageName: `${pageType}:${pageDetail}:n/a:*`,
            pageType,
            pageDetail,
            previousPageName: contextEvent.pageName || getProp(digitalData, 'page.attributes.sephoraPageInfo.pageName')
        });
    };

    const showModal = (installmentValue, totalAmount, selectedPaymentMethod) => {
        if (showCreditCardOffer) {
            dispatch(Actions.showCreditCardOfferModal({ isOpen: true }));
        } else {
            dispatch(
                Actions.showBuyNowPayLaterModal({
                    isOpen: true,
                    installmentValue,
                    totalAmount,
                    isAfterpayEnabled,
                    isKlarnaEnabled,
                    selectedPaymentMethod,
                    isPayPalPayLaterEligibleEnabled,
                    showPDPTopOfPageRedesign
                })
            );
        }

        deferTaskExecution(() => {
            triggerAsyncPageLoadEvent();
            productPageSOTBindings.seeDetails();
        });
    };

    const buildMessage = () => {
        const [installmentValue] = skuHelpers.getInstallmentValue(sku);
        const offerTextKey = showCreditCardOffer
            ? 'testCardDiscount'
            : showPDPTopOfPageRedesign
                ? 'installmentsAfterpayAndKlarna'
                : 'installmentsWithAfterpayAndKlarna';
        const reducedAmount = showCreditCardOffer ? discountValue : [installmentValue.replace(' ', '&nbsp;')];
        let klarnaInfoMessageHtml = getText(offerTextKey, reducedAmount);

        if (!showCreditCardOffer) {
            const boldTextKey = showPDPTopOfPageRedesign ? 'installmentsAfterpayAndKlarna' : 'boldInstallmentsWithAfterpayAndKlarna';
            klarnaInfoMessageHtml = getText(boldTextKey, reducedAmount);
            const htmlContent = showPDPTopOfPageRedesign ? klarnaInfoMessageHtml : klarnaInfoMessageHtml.replace(/\*(.*?)\*/g, '<strong>$1</strong>');

            return (
                <Text
                    css={styles.paymentsBadge}
                    data-at={Sephora.debug.dataAt('klarna_info_message')}
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                />
            );
        }

        return (
            <Text
                color={showCreditCardOffer && 'gray'}
                data-at={Sephora.debug.dataAt('klarna_info_message')}
                dangerouslySetInnerHTML={{ __html: klarnaInfoMessageHtml }}
            />
        );
    };

    // Productionalize only for US and small UI according to CRMTS-170
    const [installmentValue, totalAmount] = skuHelpers.getInstallmentValue(sku);

    if (!installmentValue) {
        return null;
    }

    const infoButton = paymentMethod => (
        <InfoButton
            size={14}
            aria-label={getText('learnMore')}
            onClick={() => showModal(installmentValue, totalAmount, paymentMethod)}
            data-at={Sephora.debug.dataAt('info_icon')}
        />
    );

    const getPaymentMethodFromLogo = paymentLogo => {
        if (paymentLogo === 'klarnaLogo') {
            return KLARNA;
        }

        if (paymentLogo === 'afterpayLogo') {
            return AFTERPAY;
        }

        if (paymentLogo === 'paypalLogo') {
            return PAYPAL;
        }

        return null;
    };

    const paymentLogos = {
        klarnaLogo: (
            <>
                <Image
                    disableLazyLoad={true}
                    src='/img/ufe/logo-klarna.svg'
                    alt='Klarna'
                    width={42}
                    height={10}
                    css={{
                        position: 'relative',
                        marginLeft: showPDPTopOfPageRedesign ? space[1] : 0
                    }}
                />{' '}
                {!showPDPTopOfPageRedesign && infoButton(KLARNA)}
            </>
        ),
        afterpayLogo: localeUtils.isCanada() ? (
            <>
                <Image
                    disableLazyLoad={true}
                    src='/img/ufe/logo-afterpay.svg'
                    alt='afterpay'
                    width={66}
                    height={12}
                    css={{
                        position: 'relative',
                        top: 3,
                        marginLeft: showPDPTopOfPageRedesign ? space[1] : 0
                    }}
                />{' '}
                {!showPDPTopOfPageRedesign && infoButton(AFTERPAY)}
            </>
        ) : (
            <>
                <Image
                    disableLazyLoad={true}
                    src='/img/ufe/logo-afterpay-us.svg'
                    alt='afterpay'
                    width={66}
                    height={16.25}
                    css={{
                        position: 'relative',
                        top: 3,
                        marginLeft: showPDPTopOfPageRedesign ? space[1] : 0
                    }}
                />{' '}
                {!showPDPTopOfPageRedesign && infoButton(AFTERPAY)}
            </>
        ),
        paypalLogo: (
            <>
                <Image
                    disableLazyLoad={true}
                    src='/img/ufe/logo-paypal.svg'
                    alt='paypal'
                    width={53}
                    height={15}
                    css={{
                        position: 'relative',
                        top: 3,
                        marginLeft: showPDPTopOfPageRedesign ? space[1] : 0
                    }}
                />{' '}
                {!showPDPTopOfPageRedesign && infoButton(PAYPAL)}
            </>
        )
    };

    const [firstPaymentLogo, secondPaymentLogo, thirdPaymentLogo] = orderUtils.getAvailablePaymentLogos(
        isAfterpayEnabled,
        isKlarnaEnabled,
        isPayPalPayLaterEligibleEnabled
    );
    const selectedPaymentMethod = getPaymentMethodFromLogo(firstPaymentLogo);

    return (
        <Text
            fontSize='sm'
            data-at={Sephora.debug.dataAt('klarna_info_block')}
            style={isKlarnaEnabled === undefined ? { visibility: 'hidden' } : null}
            css={showPDPTopOfPageRedesign ? styles.redesignBlock : null}
        >
            {buildMessage()}{' '}
            {showCreditCardOffer && (
                <Link
                    color='blue'
                    underline={true}
                    onClick={() => showModal(installmentValue, totalAmount)}
                    children={getText('testSeeDetails')}
                />
            )}
            {!showCreditCardOffer && firstPaymentLogo ? (
                <>
                    {showPDPTopOfPageRedesign ? (
                        <>
                            {infoButton(selectedPaymentMethod)} {paymentLogos[firstPaymentLogo]}
                        </>
                    ) : (
                        <> {paymentLogos[firstPaymentLogo]}</>
                    )}
                    <span css={!summaryDropdownDisplay && !summaryDoubleDisplay && !showHighlightBnplOnPdp && { whiteSpace: 'nowrap' }}>
                        {secondPaymentLogo && (
                            <>
                                {!showPDPTopOfPageRedesign && ` ${getText('or')} `}
                                {paymentLogos[secondPaymentLogo]}
                            </>
                        )}
                    </span>
                    {thirdPaymentLogo && (
                        <span css={!summaryDropdownDisplay && !summaryDoubleDisplay && !showHighlightBnplOnPdp && { whiteSpace: 'nowrap' }}>
                            {thirdPaymentLogo && (
                                <>
                                    {!showPDPTopOfPageRedesign && ` ${getText('or')} `}
                                    {paymentLogos[thirdPaymentLogo]}
                                </>
                            )}
                        </span>
                    )}
                </>
            ) : null}
        </Text>
    );
};

const styles = {
    paymentsBadge: {
        display: 'inline-flex',
        backgroundColor: colors.lightBlue,
        borderRadius: radii[2],
        padding: `2px ${space[2]}px`,
        marginRight: '3px'
    },
    redesignBlock: {
        flexBasis: '100%',
        marginBottom: space[2]
    }
};

export default wrapFunctionalComponent(KlarnaMarketing, 'KlarnaMarketing');
